<script>
import { useAlert } from 'dashboard/composables';

import CreateScheduleModal from '../../../branches/components/createScheduleModal.vue';
import ViewWorkingHours from '../../../branches/components/viewWorkingHours.vue';
import ViewAssignedStaff from '../../../branches/components/viewAssignedStaff.vue';
// import ViewOverrides from '../Overrides/viewOverrides.vue';
import ViewOverrides from '../Overrides/viewOverridesV2.vue';
import DeleteConfirmationModal from '../../../branches/components/deleteConfirmationModal.vue';
import ScheduleStaff from '../ScheduleStaff.vue';

export default {
  name: 'StaffScheduleTab',
  components: {
    CreateScheduleModal,
    ViewWorkingHours,
    ViewAssignedStaff,
    ViewOverrides,
    DeleteConfirmationModal,
    ScheduleStaff,
  },

  props: {
    branchId: {
      type: [String, Number],
      required: true,
    },
  },

  watch: {
    selectedScheduleId(newVal) {
      console.log('Schedule id', newVal);
    }
  },

  data() {
    return {
      isCreatingSchedule: false,
      isEditingSchedule: false,
      editScheduleId: null,
      selectedScheduleId: null,
      deleteScheduleObject: null,
      isLoading: false,
      showDeleteModal: false,
    };
  },

  computed: {
    isOnboardingSetupSchedule() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'setup_staff_schedule';
    },
    isOnboardingLinkStaffToSchedule() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'link_staff_to_schedule';
    },
    scheduleList() {
      return this.$store.state.schedules.schedules || [];
    },

    selectedSchedule() {
      if (this.selectedScheduleId) {
        return this.scheduleList.find(s => s.id === this.selectedScheduleId);
      }
      return null;
    },

    selectedStaffList() {
      if(this.selectedSchedule === null) return;
      return this.selectedSchedule.staff;
    }
  },

  methods: {
    handleOpenDeleteSchedule(schedule) {
      this.showDeleteModal = true;
      this.deleteScheduleObject = { id: schedule.id, name: schedule.name };
    },
    handleCloseDeleteSchedule() {
      this.showDeleteModal = false;
      this.deleteScheduleObject = null;
    },
    async resetCreateSchedule() {
      this.isCreatingSchedule = false;
      this.isEditingSchedule = false;
      this.editScheduleId = null;

      if (this.scheduleList?.length > 0) {
        this.selectedScheduleId = this.scheduleList[0].id;
      }
    },

    async loadOverrides() {
      try {
        await this.$store.dispatch(
          'override/fetchOverrides',
          this.selectedScheduleId
        );
      } catch (err) {
        console.error('Error loading override:', err);
        useAlert(err);
        this.overrideList = [];
      }
    },

    handleEditSchedule(scheduleId) {
      this.isEditingSchedule = true;
      this.editScheduleId = scheduleId;
    },

    async handleDeleteSchedule(scheduleId) {
      try {
        await this.$store.dispatch('schedules/deleteSchedule', scheduleId);
        await this.$store.dispatch('schedules/fetchSchedules');
      } catch (err) {
        console.error(err);
        useAlert(err);
      } finally {
        if (this.scheduleList?.length > 0) {
          this.selectedScheduleId = this.scheduleList[0].id;
          await this.$store.dispatch('schedules/fetchWorkingHours', this.selectedScheduleId);
        }
        this.handleCloseDeleteSchedule();
      }
    },

    async refreshSelectedSchedule() {
      await this.$store.dispatch('schedules/fetchSchedules');
    },

    async handleAfterCreate(newScheduleId) {
      this.selectedScheduleId = newScheduleId;
      await this.refreshSelectedSchedule();
    }
  },

  async mounted() {
    try {
      this.isLoading = true;

      await this.$store.dispatch('schedules/updateFilters', { branch_id: this.branchId });

      if (this.scheduleList?.length > 0) {
        this.selectedScheduleId = this.scheduleList[0].id;
        await this.loadOverrides();
      }
    } catch (err) {
      console.error(err);
      useAlert(err);
    } finally {
      this.isLoading = false;
    }
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 mb-8 px-8">
    <!-- Schedule Creation Modal -->
    <div 
      v-if="isCreatingSchedule || isEditingSchedule" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <CreateScheduleModal 
        :isEditingSchedule="isEditingSchedule" 
        :branchId="branchId" 
        :editScheduleId="editScheduleId" 
        :resetCreateSchedule="resetCreateSchedule"
        @created="handleAfterCreate"
      />
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 flex items-center justify-center h-[100vh] w-[100vw] bg-slate-900/50 dark:bg-slate-800/50 z-50"
    >
      <DeleteConfirmationModal
        :deleteName="deleteScheduleObject.name"
        @confirm-delete="handleDeleteSchedule(deleteScheduleObject.id)"
        @close="handleCloseDeleteSchedule"
      />
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center">
      <h1 class="text-xl font-semibold text-slate-900 dark:text-white">
        {{ $t('BRANCHES.VIEW_BRANCH.SCHEDULES') }}
      </h1>
      <div class="relative inline-block">
        <woot-button
          variant="smooth"
          color-scheme="primary"
          icon="plus-sign"
          @click="isCreatingSchedule = true"
        >
          {{ $t('BRANCHES.VIEW_BRANCH.CREATE_SCHEDULE') }}
        </woot-button>
        <!-- Onboarding Hint Tooltip -->
        <div v-if="isOnboardingSetupSchedule" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
          <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
            Hint: Click to create a staff schedule <fluent-icon icon="caret-up" />
          </div>
        </div>
      </div>
    </div>

    <!-- Schedule List -->
    <div class="flex gap-4 overflow-auto">
      <button 
        v-for="(schedule, index) in scheduleList" 
        :key="index" 
        @click="selectedScheduleId = schedule.id"
        class="px-5 py-3 border-2 border-slate-500 rounded-lg cursor-pointer relative group my-4"
        :class="{'bg-woot-500 text-white': selectedScheduleId === schedule.id}"
      >
        {{ schedule.name }}
        <span 
          class="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          @click.stop="handleOpenDeleteSchedule(schedule)"
        >
          <fluent-icon icon="dismiss-circle" />
        </span>
      </button>
    </div>

    <!-- Schedule Details -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" v-if="scheduleList.length > 0">
      <div v-if="!selectedSchedule" class="text-center text-slate-500 dark:text-slate-300 py-6 bg-slate-100 dark:bg-slate-800 rounded-md shadow-sm">
        <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_SCHEDULE_SELECTED') }}</p>
      </div>

      <ViewWorkingHours 
        v-if="selectedSchedule" 
        :scheduleId="selectedScheduleId" 
        :selectedSchedule="selectedSchedule" 
        @refreshSelectedSchedule="refreshSelectedSchedule"
      />

      <div v-if="!isLoading" class="flex flex-col gap-6">
        <!-- <ViewAssignedStaff :scheduleId="selectedScheduleId" :branchId="branchId" /> -->
        <ViewOverrides :scheduleId="selectedScheduleId" @refresh="refreshSelectedSchedule"/>
        <ScheduleStaff :staff-list="selectedStaffList" :is-loading="isLoading" :schedule-id="selectedScheduleId" @refresh="refreshSelectedSchedule" />
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="scheduleList.length === 0" class="text-center text-slate-500 dark:text-slate-300 py-6 bg-slate-100 dark:bg-slate-800 rounded-md shadow-sm">
      <p>{{ $t('BRANCHES.VIEW_BRANCH.NO_SCHEDULE_ADDED') }}</p>
    </div>
  </div>
</template>